import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TypeaheadDirective } from 'ngx-bootstrap';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import { Doctor } from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { Patient } from '../../core/api/patient.service';
import { PatientSearchService, SearchablePatient } from './patient-search.service';

@Component({
  selector: 'csi-patient-lookup',
  templateUrl: './patient-lookup.component.html',
  styleUrls: ['./patient-lookup.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientLookupComponent implements OnDestroy, AfterViewInit {
  @Input() doctor: Doctor;
  @Input() placeholder: string;
  @Input() addNewPatients = false;
  @Input() clearOnSelect = false;
  // 'outlined' renders the lookup as the same Material outlined field csi-search-bar uses,
  // backed by a mat-autocomplete instead of ng-select. Both read the same patient list.
  @Input() appearance: 'underline' | 'outlined' = 'underline';
  // Already translated by the caller, like `placeholder`. Only the outlined look shows it.
  @Input() label: string;
  @Input() set disabled(disabled: boolean) {
    if (disabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }
  @Input() displayContactInformation: boolean;
  @Input() appendTo: string = 'body';
  @Output() selectedPatient: EventEmitter<Partial<Patient>> = new EventEmitter<Partial<Patient>>();
  @Output() createNewPatient: EventEmitter<string> = new EventEmitter<string>();
  // Not static: the ng-select now lives in the template behind the appearance switch, so it
  // only exists once the view has been built.
  @ViewChild('ngSelectComponent', { static: false }) hostNgSelectComponent: any;

  public isServerErroring = false;
  public _loading = true;
  public get loading() {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.ngZone.runOutsideAngular(() =>
      setTimeout(() => this.changeDetectorRef.detectChanges(), 0)
    );
  }

  public searchControl = new FormControl();
  // The outlined look holds the typed text here; ng-select instead puts the selected option
  // into `searchControl`, so the two cannot share one control.
  public searchTermControl = new FormControl('');
  public filteredPatients$: Observable<SearchablePatient[]>;
  public searchTerm$ = new Subject<string>();
  // mat-autocomplete has no virtual scroll, so the panel shows the best matches rather than
  // every patient in the clinic.
  public maxAutocompleteOptions = 20;

  @ViewChild(TypeaheadDirective, { static: false }) typeAheadDirective: TypeaheadDirective;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private patientSearchService: PatientSearchService
  ) {
    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(searchablePatient => !!searchablePatient)
      )
      .subscribe((searchablePatient: SearchablePatient) => {
        this.onSelect(searchablePatient);
      });

    this.searchTermControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => this.searchTerm$.next(typeof value === 'string' ? value : ''));

    // Initialize
    this.filteredPatients$ = this.searchFor('').pipe(
      tap(() => {
        this.filteredPatients$ = this.searchTerm$.pipe(
          debounceTime(400),
          distinctUntilChanged(),
          switchMap((term: string) => this.searchFor(term)),
          untilDestroyed(this)
        );
      })
    );
  }

  ngAfterViewInit() {
    if (this.addNewPatients && this.hostNgSelectComponent) {
      // Code partly taken from https://github.com/ng-select/ng-select/commit/d5e97af72201a3fbedb1fa78d6c69be61776c872
      // See feature request https://github.com/ng-select/ng-select/issues/1232
      Object.defineProperty(this.hostNgSelectComponent, 'showAddTag', {
        get: this.showAddTagRewrite
      });
    }
  }

  ngOnDestroy() {}

  private onSelect(patient: SearchablePatient | string): void {
    if ((patient as SearchablePatient).patient) {
      this.selectedPatient.emit((patient as SearchablePatient).patient);
    } else {
      this.createNewPatient.emit((patient as SearchablePatient).fullName || (patient as string));
    }
  }

  private showAddTagRewrite = (): boolean => {
    return this.hostNgSelectComponent.searchTerm && this.hostNgSelectComponent.addTag;
  };

  private searchFor(term: string): Observable<SearchablePatient[]> {
    const searchValue = this.patientSearchService.normalizeSearchTerm(term);
    this.loading = true;
    return this.patientSearchService.searchablePatients(this.doctor).pipe(
      map(searchablePatients => {
        this.isServerErroring = false;
        this.loading = false;
        return this.patientSearchService.matching(searchValue, searchablePatients);
      }),
      catchError(err => {
        this.isServerErroring = true;
        this.loading = false;
        ErrorHandlerService.trySendingSentryError(err);
        return of([]);
      })
    );
  }

  public getNotFoundText(): string {
    return this.isServerErroring
      ? 'Server or connection error. Please try again.'
      : 'No patients found.';
  }

  onClose() {
    if (this.clearOnSelect) {
      this.searchControl.setValue('');
    }
  }

  public get searchTerm(): string {
    const value = this.searchTermControl.value;
    return typeof value === 'string' ? value : '';
  }

  public get canAddNewPatient(): boolean {
    return this.addNewPatients && !this.isServerErroring && !!this.searchTerm.trim();
  }

  // mat-autocomplete calls this unbound, so it must not touch `this`.
  public displaySearchablePatient(searchablePatient: SearchablePatient): string {
    return searchablePatient ? searchablePatient.fullName : '';
  }

  /** A null option is the "add new patient" row, which carries no patient of its own. */
  public onAutocompleteSelect(searchablePatient: SearchablePatient | null) {
    const searchTerm = this.searchTerm;

    if (this.clearOnSelect) {
      this.searchTermControl.setValue('');
    }

    this.onSelect(searchablePatient || searchTerm);
  }

  public clearSearchTerm() {
    this.searchTermControl.setValue('');
  }
}
